import { useEffect, useMemo } from 'react';
import { Field, useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { OPTIONAL_EXTRA_PRODUCT_TYPE_MAPPING } from 'utils/constant';
import { price } from 'utils/helpers';
import StepperField from 'components/formik/stepper_field/stepper_field';
import { useBulkQuery } from 'redux/api_slice/availability';

const OptionalExtras = ({ resource }) => {
  const { values, setFieldValue } = useFormikContext();
  const { date, optionalExtras = {} } = values;
  const group = useMemo(() => {
    const resourceGroups = resource.resource_groups || [];

    return resourceGroups.find(
      (item) =>
        item.product_type_id ===
        OPTIONAL_EXTRA_PRODUCT_TYPE_MAPPING[
          ecom.craftConfig?.site || ecom.craftConfig?.tenant
        ]
    );
  }, [resource]);
  const hasOptionalExtras = Boolean(group?.package_items?.length);

  useEffect(() => {
    if (hasOptionalExtras) {
      setFieldValue('optionalExtraGroupId', group.id);
    }
  }, [setFieldValue, group, hasOptionalExtras]);

  const optionalExtraResources = {};
  Object.entries(optionalExtras).forEach(([itemId, count]) => {
    const item = group?.package_items.find(
      (packageItem) => packageItem.id === Number(itemId)
    );
    if (item && count > 0) {
      optionalExtraResources[item.resource.id] = count;
    }
  });

  const {
    data: bookability = {},
    isFetching,
    isSuccess,
  } = useBulkQuery(
    {
      resourceIds: optionalExtraResources,
      fromDate: date,
      toDate: date,
      units: 0,
    },
    {
      skip: !hasOptionalExtras || !date || isEmpty(optionalExtraResources),
    }
  );

  const unavailableResourceIds = isEmpty(optionalExtraResources)
    ? []
    : Object.entries(bookability)
        .filter(([, availability]) => availability[date]?.available === false)
        .map(([resourceId]) => Number(resourceId));
  const optionalExtraUnavailable =
    isFetching || (isSuccess && unavailableResourceIds.length > 0);

  useEffect(() => {
    setFieldValue('optionalExtraUnavailable', optionalExtraUnavailable ? 1 : 0);
  }, [setFieldValue, optionalExtraUnavailable]);

  if (!hasOptionalExtras) {
    return null;
  }

  return (
    <div className="border-top py-4">
      <h4 className="mb-4">Optional Extras</h4>
      {group.package_items.map(({ id, resource: resourceItem }, i) => {
        const name = `optionalExtras.${id}`;
        return (
          <div
            key={name}
            className={`item-selection${
              i !== group.package_items.length - 1 ? ' mb-4' : ''
            }`}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="item-selection__title">
                <div className="d-flex align-items-center">
                  <label className="lh-sm" htmlFor={name}>
                    <strong>{resourceItem.name}</strong>
                  </label>
                </div>
                <p className="mt-2">
                  {price(resourceItem.todays_price_in_cents)}
                </p>
              </div>
              <div className="item-selection__stepper">
                <Field component={StepperField} name={name} />
              </div>
            </div>
            {unavailableResourceIds.includes(resourceItem.id) &&
              resourceItem.id in optionalExtraResources && (
                <div className="text-danger text-x-small mt-3">
                  The product is unavailable, please adjust the quantity or try
                  an alternate date
                </div>
              )}
          </div>
        );
      })}
    </div>
  );
};

OptionalExtras.propTypes = {
  resource: PropTypes.shape({
    resource_groups: PropTypes.arrayOf(
      PropTypes.shape({
        product_type_id: PropTypes.number,
        package_items: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number,
            resource: PropTypes.shape({
              name: PropTypes.string,
              todays_price_in_cents: PropTypes.number,
            }),
          })
        ),
      })
    ),
  }),
};

export default OptionalExtras;
